import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Download, Info, AlertTriangle } from 'lucide-react';
import { getOccupancy, OccupancyResp, OccupancyRow } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import CountUp from '../../../components/CountUp';
import EmptyState from '../../../components/EmptyState';
import { nivelOcupacion } from '../../../utils/ocupacion';

/** Color de la barra/etiqueta según ocupación (umbrales en utils/ocupacion.ts, compartidos
 *  con el calendario para que el mismo % no signifique cosas distintas en cada pantalla). */
function tone(pct: number) {
  switch (nivelOcupacion(pct)) {
    case 'sobrecupo': return { bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' };
    case 'alta': return { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' };
    case 'media': return { bar: 'bg-brand-blue', text: 'text-content' };
    default: return { bar: 'bg-red-500', text: 'text-red-600 dark:text-red-400' };
  }
}

function exportCsv(r: OccupancyResp) {
  const lines = ['Clase,Estilo,Profesor,Inscritos,Cupo,Ocupación %'];
  for (const x of r.data) {
    lines.push([`"${x.name}"`, `"${x.style || ''}"`, `"${x.teacher || ''}"`, x.enrolled, x.capacity, x.pct].join(','));
  }
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'ocupacion-clases-letsmove.csv'; a.click();
  URL.revokeObjectURL(url);
}

function ClassRow({ r }: { r: OccupancyRow }) {
  const t = tone(r.pct);
  return (
    <Link to={`/clases/${r.class_id}`} className="block rounded-xl border border-line bg-surface p-3 transition hover:bg-surface-2/60">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-content">{r.name}</div>
          <div className="truncate text-xs text-content-3">{r.style || '—'}{r.teacher ? ` · ${r.teacher}` : ''}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {r.pct > 100 && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400"><AlertTriangle size={10} /> sobrecupo</span>}
          <span className="text-sm text-content-2">{r.enrolled}/{r.capacity}</span>
          <span className={`w-11 text-right font-display text-sm font-bold ${t.text}`}>{r.pct}%</span>
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
        {/* Karla 30-sep: la barrita de cupo va neutra en todo el sistema; el color de ocupación vive en el %. */}
        <div className="h-full rounded-full bg-content" style={{ width: `${Math.min(100, r.pct)}%` }} />
      </div>
    </Link>
  );
}

export default function OccupancyReport() {
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [data, setData] = useState<OccupancyResp | null>(() => readCache<OccupancyResp>('rep-occupancy') ?? null);
  const [loading, setLoading] = useState(() => !readCache('rep-occupancy'));

  useEffect(() => {
    getOccupancy().then((r) => { setData(r); writeCache('rep-occupancy', r); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <Skeleton className="h-96 w-full rounded-2xl" />;
  if (data.data.length === 0) return <EmptyState icon={CalendarDays} title="Sin clases con inscritos" subtitle="Aún no hay rosters cargados." />;

  const s = data.summary;
  const styleMax = Math.max(1, ...data.byStyle.map((x) => x.enrolled));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <button onClick={() => exportCsv(data)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 hover:bg-surface-2">
          <Download size={15} /> CSV
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Ocupación</div>
          <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={s.occupancy_pct} />%</div>
          <div className="mt-1 text-xs text-content-3">{s.total_enrolled} de {s.total_capacity} cupos</div>
        </div>
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Clases operativas</div>
          <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={s.operating_classes} /></div>
          <div className="mt-1 text-xs text-content-3">con alumnos inscritos</div>
        </div>
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Llenas (≥90%)</div>
          <div className="mt-1 font-display text-2xl font-bold text-emerald-600 dark:text-emerald-400"><CountUp value={s.full} /></div>
          <div className="mt-1 text-xs text-content-3">a tope o sobrecupo</div>
        </div>
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Con cupo (&lt;50%)</div>
          <div className="mt-1 font-display text-2xl font-bold text-red-600 dark:text-red-400"><CountUp value={s.near_empty} /></div>
          <div className="mt-1 text-xs text-content-3">oportunidad de llenar</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Lista de clases por ocupación */}
        <div className="min-w-0 lg:col-span-3">
          <div className="mb-2 text-sm font-medium text-content-2">Clases por ocupación</div>
          <div className="space-y-2">
            {data.data.map((r) => <ClassRow key={r.class_id} r={r} />)}
          </div>
        </div>

        {/* Por estilo */}
        <div className="min-w-0 lg:col-span-2">
          <div className="mb-2 text-sm font-medium text-content-2">Por estilo</div>
          <div className="space-y-3 rounded-2xl border border-line bg-surface p-5 shadow-sm">
            {data.byStyle.map((x) => (
              <div key={x.style}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="truncate text-content-2">{x.style} <span className="text-content-3">({x.classes})</span></span>
                  <span className="font-medium text-content">{x.enrolled}/{x.capacity}
                    <span className="ml-1.5 text-xs text-content-3">{x.pct}%</span>
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
                  <div className="h-full rounded-full bg-brand-blue" style={{ width: `${(x.enrolled / styleMax) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Nota de clases sin inscritos */}
      {s.empty_classes > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-line bg-surface-2/40 p-3 text-xs text-content-3">
          <Info size={15} className="mt-0.5 shrink-0 text-content-3" />
          <span>
            {s.empty_classes} clases activas sin inscritos (rentas de salón, ensayos o clases sin roster importado)
            no se cuentan en la ocupación. La asistencia real se reflejará al pasar lista.
          </span>
        </div>
      )}
    </div>
  );
}
